import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add,
  Check,
  Close,
  DeleteOutline,
  Edit,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

// setRegionGroupName/setRegionGroupVisible mutate the group in place without
// notifying the host (a known audiodaw gap — createRegionGroup/
// removeRegionGroup do, the other group-property setters don't). Re-asserting
// the active group forces useAudioDAWState's onActiveRegionGroupChange
// handler to re-pull getRegionGroups(), which keeps the panel live in the
// meantime. Drop this once that's fixed upstream.
function refreshRegionGroups(daw) {
  const activeId = daw.getActiveRegionGroupId();
  if (activeId) daw.setActiveRegionGroup(activeId);
}

function RegionGroupRow({ daw, group, isActive, isSavingAnnotations }) {
  const isNew = group.name === "";
  const [mode, setMode] = useState(null); // null | "renaming" | "deleting"
  const [draftName, setDraftName] = useState(group.name);
  const editingName = isNew || mode === "renaming";
  const confirmingDelete = mode === "deleting";

  // Keep the draft in sync with the real name whenever we're not the one
  // editing it (e.g. it changed from elsewhere, or we just finished saving).
  useEffect(() => {
    if (!editingName) {
      setDraftName(group.name);
    }
  }, [group.name, editingName]);

  const saveName = () => {
    const trimmed = draftName.trim();
    if (!trimmed) return;
    daw.setRegionGroupName(group.id, trimmed);
    refreshRegionGroups(daw);
    setMode(null);
  };

  const toggleVisible = () => {
    daw.setRegionGroupVisible(group.id, !group.visible);
    refreshRegionGroups(daw);
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      onClick={() => daw.setActiveRegionGroup(group.id)}
      sx={{
        p: 0.5,
        borderRadius: 1,
        cursor: "pointer",
        backgroundColor: (theme) =>
          isActive ? theme.palette.action.selected : "transparent",
        "&:hover": {
          backgroundColor: (theme) => theme.palette.action.hover,
        },
      }}
    >
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: "50%",
          backgroundColor: group.color,
          flexShrink: 0,
        }}
      />
      {confirmingDelete ? (
        <>
          <Typography
            variant="body2"
            noWrap
            sx={{
              flex: 1,
              height: 40,
              display: "flex",
              alignItems: "center",
            }}
          >
            Delete {group.name ? `"${group.name}"` : "this group"}?
          </Typography>
          <IconButton
            disabled={isSavingAnnotations}
            size="small"
            color="error"
            onClick={(event) => {
              event.stopPropagation();
              daw.removeRegionGroup(group.id);
            }}
          >
            <Check fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setMode(null);
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </>
      ) : (
        <>
          {editingName ? (
            <>
              <TextField
                size="small"
                autoFocus
                placeholder="Group name"
                value={draftName}
                onClick={(event) => event.stopPropagation()}
                onChange={(event) => setDraftName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") saveName();
                }}
                sx={{ flex: 1 }}
              />
              <IconButton
                size="small"
                disabled={!draftName.trim()}
                onClick={(event) => {
                  event.stopPropagation();
                  saveName();
                }}
              >
                <Check fontSize="small" />
              </IconButton>
            </>
          ) : (
            <>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  flex: 1,
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {group.name}
              </Typography>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  setMode("renaming");
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </>
          )}
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              toggleVisible();
            }}
          >
            {group.visible ? (
              <Visibility fontSize="small" />
            ) : (
              <VisibilityOff fontSize="small" />
            )}
          </IconButton>
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setMode("deleting");
            }}
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
        </>
      )}
    </Stack>
  );
}

export default function RegionGroupsPanel({
  daw,
  regionGroups,
  activeRegionGroupId,
  isSavingAnnotations,
}) {
  const addGroup = () => {
    const groupId = daw.createRegionGroup("");
    daw.setActiveRegionGroup(groupId);
  };

  return (
    <Card elevation={4} sx={{ p: 1.5, width: 300 }}>
      <Stack gap={1} mb={1}>
        <Typography variant="subtitle2" sx={{ textTransform: "uppercase" }}>
          Region Groups
        </Typography>
        <Button
          size="small"
          startIcon={<Add />}
          onClick={addGroup}
          disabled={!daw || isSavingAnnotations}
          variant="outlined"
        >
          New Group
        </Button>
      </Stack>
      {regionGroups.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No region groups created.
        </Typography>
      )}
      <Divider sx={{ my: 1 }} />
      <Stack
        spacing={0.5}
        sx={{ maxHeight: `calc(100vh - 306px)`, overflowY: "auto" }}
      >
        {regionGroups.map((group) => (
          <RegionGroupRow
            key={group.id}
            daw={daw}
            group={group}
            isActive={group.id === activeRegionGroupId}
          />
        ))}
      </Stack>
    </Card>
  );
}
